/**
 * 青蓮劍歌特效系統
 * B級特殊效果，劍氣具有指向性，朝向被翻轉的棋子方向發射
 */

export interface Position {
  x: number;
  y: number;
}

export interface PetalConfig {
  x: number;
  y: number;
  angle: number;
  scale: number;
  life: number;
  velocity: number;
  rotation: number;
  acceleration: number;
}

export interface SwordBeamConfig {
  x: number;
  y: number;
  angle: number;
  targetPosition?: Position;
}

/**
 * 青蓮花瓣粒子
 */
export class LotusePetal {
  public x: number;
  public y: number;
  public angle: number;
  public scale: number;
  public life: number;
  public maxLife: number;
  public velocity: number;
  public rotation: number;
  public acceleration: number;
  public dead = false;
  private time = 0;

  constructor(config: PetalConfig) {
    this.x = config.x;
    this.y = config.y;
    this.angle = config.angle;
    this.scale = config.scale;
    this.life = config.life;
    this.maxLife = config.life;
    this.velocity = config.velocity;
    this.rotation = config.rotation;
    this.acceleration = config.acceleration;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    if (this.time > this.life) {
      this.dead = true;
      return;
    }

    // 更新位置
    this.x += Math.cos(this.angle) * this.velocity * deltaTime;
    this.y += Math.sin(this.angle) * this.velocity * deltaTime;

    // 更新旋轉
    this.acceleration += this.rotation * deltaTime;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const alpha = 1 - this.time / this.life;

    ctx.save();
    ctx.globalAlpha = alpha * 0.9;
    ctx.translate(this.x, this.y);
    ctx.rotate(this.acceleration);
    ctx.scale(this.scale, this.scale);

    // 繪製花瓣形狀
    this.drawPetal(ctx);

    ctx.restore();
  }

  private drawPetal(ctx: CanvasRenderingContext2D): void {
    const gradient = ctx.createLinearGradient(0, 0, 80, 55);
    gradient.addColorStop(0, 'rgba(180,255,220,0.9)');
    gradient.addColorStop(1, 'rgba(120,220,180,0.1)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(12, 25);
    ctx.bezierCurveTo(32, 8, 58, 12, 65, 25);
    ctx.bezierCurveTo(58, 38, 32, 42, 12, 25);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * 劍氣束
 */
export class GreenSwordBeam {
  public x: number;
  public y: number;
  public angle: number;
  public dead = false;
  private time = 0;
  private maxTime = 0.5;
  private speed = 600;

  constructor(config: SwordBeamConfig) {
    this.x = config.x;
    this.y = config.y;
    this.angle = config.angle;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    if (this.time > this.maxTime) {
      this.dead = true;
      return;
    }

    // 沿著角度方向移動
    this.x += Math.cos(this.angle) * this.speed * deltaTime;
    this.y += Math.sin(this.angle) * this.speed * deltaTime;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.angle);

    // 繪製劍氣束
    this.drawBeam(ctx);

    ctx.restore();
  }

  private drawBeam(ctx: CanvasRenderingContext2D): void {
    const gradient = ctx.createLinearGradient(0, 0, 160, 0);
    gradient.addColorStop(0, 'rgba(200,255,230,0)');
    gradient.addColorStop(0.3, 'rgba(200,255,230,0.8)');
    gradient.addColorStop(0.8, 'rgba(200,255,230,0.8)');
    gradient.addColorStop(1, 'rgba(200,255,230,0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(-80, -6, 160, 12);
  }
}

/**
 * 青蓮劍歌主效果
 */
export class GreenLotusEffect {
  public x: number;
  public y: number;
  public dead = false;
  private time = 0;
  private phase = 0;
  private petals: LotusePetal[] = [];
  private swords: GreenSwordBeam[] = [];
  private targetPositions: Position[];

  constructor(x: number, y: number, targetPositions: Position[] = []) {
    this.x = x;
    this.y = y;
    this.targetPositions = targetPositions;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    switch (this.phase) {
      case 0: // 蓮花綻放階段
        if (this.time > 0.5) {
          this.createPetals();
          this.phase = 1;
          this.time = 0;
        }
        break;

      case 1: // 劍束發射階段
        if (this.time > 0.4) {
          this.createSwordBeams();
          this.phase = 2;
          this.time = 0;
        }
        break;

      case 2: // 效果結束階段
        if (this.time > 1.5) {
          this.dead = true;
        }
        break;
    }

    // 更新花瓣
    this.petals.forEach(petal => petal.update(deltaTime));
    this.petals = this.petals.filter(petal => !petal.dead);

    // 更新劍束
    this.swords.forEach(sword => sword.update(deltaTime));
    this.swords = this.swords.filter(sword => !sword.dead);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    // 繪製光暈效果
    this.drawAura(ctx);

    // 繪製青蓮花瓣 (phase 0 主要顯示)
    if (this.phase === 0) {
      this.drawLotusFlower(ctx);
    }

    // 繪製花瓣粒子
    this.petals.forEach(petal => petal.draw(ctx));

    // 繪製劍束
    this.swords.forEach(sword => sword.draw(ctx));
  }

  private drawAura(ctx: CanvasRenderingContext2D): void {
    let alpha = 1;

    if (this.phase === 0) {
      alpha = Math.min(1, this.time / 0.5); // 淡入
    } else if (this.phase === 2) {
      alpha = Math.max(0, 1 - this.time / 1.5); // 淡出
    }

    ctx.save();
    ctx.globalAlpha = alpha;

    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, 130);
    gradient.addColorStop(0, 'rgba(180,255,220,0.8)');
    gradient.addColorStop(1, 'rgba(180,255,220,0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, 130, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawLotusFlower(ctx: CanvasRenderingContext2D): void {
    const petalCount = 8;

    for (let i = 0; i < petalCount; i++) {
      const angle = (i * Math.PI * 2) / petalCount + this.time * 1.5;

      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate(angle);

      // 直接繪製花瓣形狀
      const gradient = ctx.createLinearGradient(0, 0, 80, 55);
      gradient.addColorStop(0, 'rgba(180,255,220,0.9)');
      gradient.addColorStop(1, 'rgba(120,220,180,0.1)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(12, 25);
      ctx.bezierCurveTo(32, 8, 58, 12, 65, 25);
      ctx.bezierCurveTo(58, 38, 32, 42, 12, 25);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }
  }

  private createPetals(): void {
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;

      this.petals.push(new LotusePetal({
        x: this.x,
        y: this.y,
        angle: angle,
        scale: 0.8 + Math.random() * 0.5,
        life: 1 + Math.random() * 0.5,
        velocity: 40 + Math.random() * 40,
        rotation: -2 + Math.random() * 4,
        acceleration: -0.5 + Math.random(),
      }));
    }
  }

  private createSwordBeams(): void {
    if (this.targetPositions.length > 0) {
      // 為每個目標位置創建定向劍束
      this.targetPositions.forEach(target => {
        const angle = Math.atan2(target.y - this.y, target.x - this.x);
        const variation = -0.1 + Math.random() * 0.2; // 添加輕微隨機偏移

        this.swords.push(new GreenSwordBeam({
          x: this.x,
          y: this.y,
          angle: angle + variation,
          targetPosition: target,
        }));
      });
    } else {
      // 如果沒有目標位置，創建環形劍束
      for (let i = 0; i < 12; i++) {
        const angle = (i * Math.PI * 2) / 12 + (-0.1 + Math.random() * 0.2);

        this.swords.push(new GreenSwordBeam({
          x: this.x,
          y: this.y,
          angle: angle,
        }));
      }
    }
  }
}